/**
 * v2 (26/10/2026; calm tabs 27/10) — the countries this paste goes to, one underlined tab each:
 * flag, country and what its review says as one mark (✓ ready · an amber count to look at · ⚠
 * can't paste · a spinner while reading; the words for screen readers and on hover); ✕ takes a
 * country off (one always stays), "Add country" puts one on.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react';

import { type TargetReview, targetStatus } from '@/features/drop-generator/components/group-copy/dialog/model/targets/target-reviews';
import { cn } from '@/lib/utils';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';

import { AddCountry } from './AddCountry';

interface Props {
  all: TargetReview[];
  activeId: string;
  nameOf: (countryKey: string) => string;
  onActive: (id: string) => void;
  onRemove: (id: string) => void;
  onAdd: (countryKey: string) => void;
}

const Mark: FC<{ t: TargetReview }> = ({ t }) => {
  const status = targetStatus(t);
  const words = <span className="sr-only">{status.label}</span>;
  if (status.tone === 'ready') return <><CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />{words}</>;
  if (status.tone === 'blocked') return <><AlertTriangle className="h-3.5 w-3.5 text-destructive" aria-hidden />{words}</>;
  if (status.tone === 'reading') return <><Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden />{words}</>;
  const count = status.label.match(/^\d+/)?.[0];
  return count ? <><span aria-hidden className="rounded-full bg-warning/15 px-1.5 text-[10.5px] font-semibold tabular-nums text-warning">{count}</span>{words}</> : <span className="text-[11px] font-normal text-warning">{status.label}</span>;
};

const Tab: FC<{ t: TargetReview; active: boolean; name: string; removable: boolean; onActive: () => void; onRemove: () => void }> = ({ t, active, name, removable, onActive, onRemove }) => (
  <div className="group relative flex shrink-0 items-center">
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onActive}
      title={targetStatus(t).label}
      data-testid={`paste-target-${t.target.countryKey}`}
      className={cn('flex items-center gap-2 rounded-md py-2.5 pl-3 text-[13px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/40', removable ? 'pr-1.5' : 'pr-3', active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground')}
    >
      <span aria-hidden>{countryToFlagEmoji(name)}</span>
      {name}
      <Mark t={t} />
    </button>
    {removable ? (
      <button type="button" aria-label={`Don't paste into ${name}`} title={`Don't paste into ${name}`} onClick={onRemove} className="mr-1.5 rounded p-0.5 text-muted-foreground transition-opacity hover:bg-muted hover:text-foreground sm:opacity-0 sm:focus:opacity-100 sm:group-hover:opacity-100">
        <X className="h-3 w-3" />
      </button>
    ) : null}
    <span aria-hidden className={cn('absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary transition-opacity duration-200', active ? 'opacity-100' : 'opacity-0')} />
  </div>
);

export const TargetTabs: FC<Props> = ({ all, activeId, nameOf, onActive, onRemove, onAdd }) => (
  <div className="flex min-w-0 flex-wrap items-center gap-x-1 border-b border-border/70">
    <div role="tablist" aria-label="Countries to paste into" className="flex min-w-0 flex-wrap items-center">
      {all.map((t) => (
        <Tab key={t.target.id} t={t} active={t.target.id === activeId} name={nameOf(t.target.countryKey)} removable={all.length > 1} onActive={() => onActive(t.target.id)} onRemove={() => onRemove(t.target.id)} />
      ))}
    </div>
    <AddCountry taken={all.map((t) => t.target.countryKey)} nameOf={nameOf} onAdd={onAdd} />
  </div>
);
